import type { BuildCFSReportDefinition, CFSReportSectionDef } from './types';

export const buildCFSReportDefinition: BuildCFSReportDefinition = async ({
  ctx,
}) => {
  const moment = (await import('moment')).default;
  const { DATE_TIME_FORMAT } = await import('@/config');
  const { buildReportHeader } = await import(
    '@/utils/reports/buildReportHeader'
  );
  const { getCachedBase64Image } = await import(
    '@/pages/Kases/Components/KasePrintModal/utils/imageHelpers'
  );
  const { pdfStyles, pageMargins } = await import(
    '@/pages/Kases/Components/KasePrintModal/utils/pdfStyles'
  );
  const { createCard, createDetailGrid, draftjsRawToPdfmakeBlocks } =
    await import(
      '@/pages/Kases/Components/KasePrintModal/utils/transformDataForPDF'
    );

  const { cfsTopSectionFields, cfsReportSections } = await import('./template');
  const { FALLBACK, safeText, narrativePlainText } = await import('./mappers');

  // --- Logo ---
  let logo = undefined as string | undefined;
  if ((ctx.agency as any)?.logo?.url) {
    try {
      logo = await getCachedBase64Image((ctx.agency as any).logo.url);
    } catch {
      // ignore
    }
  }

  // --- Repeating page header (every page) ---
  const header = (): any => {
    // Report-specific entries (rebuilt per page — pdfMake mutates header nodes)
    const reportEntries: any[] = [
      {
        text: 'CFS REPORT',
        style: 'headerCaseInfo',
        margin: [0, 0, 0, 12] as [number, number, number, number],
      },
      {
        text: `Printed on: ${moment(ctx.printedAt).format(DATE_TIME_FORMAT)}`,
        style: 'headerTimestamp',
        margin: [0, 0, 0, 0] as [number, number, number, number],
      },
      {
        text: `Printed by: ${ctx.user?.fullName || ''}`,
        style: 'headerTimestamp',
        margin: [0, 0, 0, 0] as [number, number, number, number],
      },
    ];

    return buildReportHeader({
      agency: ctx.agency,
      logo,
      reportEntries,
      innerLayoutPadding: { h: 3, v: 2 },
    });
  };

  // --- Footer ---
  const footer: any = (currentPage: number, pageCount: number) => ({
    text: `Page ${currentPage} of ${pageCount}`,
    alignment: 'center',
    style: 'footer',
    margin: [0, 10, 0, 0] as [number, number, number, number],
  });

  // --- Helper: detail grid with variable widths (single multi-row table) ---
  const createDetailGridWithWidths = (
    fields: Array<{
      label: string;
      value: any;
      width?: string | number;
      colSpan?: number;
    }>,
    columnsPerRow: number,
  ) => {
    const allRows: any[][] = [];
    let tableWidths: (string | number)[] | null = null;

    // ColSpan-aware chunking: group fields so each row's total span ≤ columnsPerRow
    const chunks: Array<typeof fields> = [];
    {
      let ci = 0;
      while (ci < fields.length) {
        const chunk: typeof fields = [];
        let colsUsed = 0;
        while (
          ci < fields.length &&
          colsUsed + (fields[ci].colSpan || 1) <= columnsPerRow
        ) {
          chunk.push(fields[ci]);
          colsUsed += fields[ci].colSpan || 1;
          ci++;
        }
        if (!chunk.length && ci < fields.length) {
          // Single field exceeds columnsPerRow — take it alone
          chunk.push(fields[ci]);
          ci++;
        }
        chunks.push(chunk);
      }
    }

    for (const chunk of chunks) {
      // Build cells with colSpan support
      const cells: any[] = [];
      const rowWidths: (string | number)[] = [];

      for (const field of chunk) {
        const span = field.colSpan || 1;
        let cell: any;
        if (field.label) {
          const isBlockContent =
            typeof field.value === 'object' &&
            field.value !== null &&
            'stack' in field.value;
          if (isBlockContent) {
            // Block-level content (e.g. rich-text narrative): label above, content below
            cell = {
              stack: [
                { text: `${field.label}: `, style: 'fieldLabel' },
                field.value,
              ],
            };
          } else {
            cell = {
              text: [
                { text: `${field.label}: `, style: 'fieldLabel' },
                typeof field.value === 'object' && field.value !== null
                  ? field.value
                  : { text: safeText(field.value), style: 'fieldValue' },
              ],
            };
          }
        } else {
          cell = { text: '', style: 'fieldValue' };
        }

        if (span > 1) cell.colSpan = span;
        cells.push(cell);
        rowWidths.push(field.width || '*');

        // Add filler cells for colSpan
        for (let s = 1; s < span; s++) {
          cells.push({});
          rowWidths.push('*');
        }
      }

      // Fill remaining cells
      while (cells.length < columnsPerRow) {
        cells.push({ text: '', style: 'fieldValue' });
        rowWidths.push('*');
      }

      // Use first row's widths for the entire table
      if (!tableWidths) {
        tableWidths = rowWidths;
      }

      allRows.push(cells);
    }

    return {
      table: {
        widths: tableWidths || [],
        body: allRows,
      },
      layout: {
        hLineWidth: () => 1,
        vLineWidth: () => 1,
        hLineColor: () => '#000',
        vLineColor: () => '#000',
        paddingLeft: () => 4,
        paddingRight: () => 4,
        paddingTop: () => 2,
        paddingBottom: () => 2,
      },
      margin: [0, 0, 0, 0] as [number, number, number, number],
    };
  };

  // --- Helpers for section rendering ---
  const valueToContent = (value: any) => {
    // Sentinel: narrative object — convert Draft.js content to pdfmake blocks
    if (value && typeof value === 'object' && '_narrativeContent' in value) {
      const n = value._narrativeContent;
      const content = n?.content;
      const raw = (() => {
        const r = content?.text ?? content;
        if (typeof r === 'string') {
          try {
            return JSON.parse(r);
          } catch {
            return null;
          }
        }
        return r?.blocks ? r : null;
      })();
      if (raw?.blocks) {
        const blocks = draftjsRawToPdfmakeBlocks(raw);
        return {
          stack: blocks.length ? blocks : [{ text: ' ', style: 'fieldValue' }],
        };
      }
      return { text: safeText(narrativePlainText(n)), style: 'fieldValue' };
    }
    return typeof value === 'object' && value !== null && 'text' in value
      ? value
      : { text: safeText(value), style: 'fieldValue' };
  };

  const layoutToFields = (layout: any, item?: any) => {
    const defs = (layout?.fields ?? []) as any[];
    const out: any[] = [];
    for (const d of defs) {
      // Hidden fields (e.g. address sub-fields for a transient person) are
      // dropped. Absorb the hidden field's colSpan into the previous visible
      // cell so the grid row keeps its total width and the following fields
      // don't reflow upward (a plain filter would pull the phone fields onto
      // the Address row). Degrades gracefully if there is no previous field.
      if (d.hidden && d.hidden(ctx, item)) {
        const prev = out[out.length - 1];
        if (prev) prev.colSpan = (prev.colSpan || 1) + (d.colSpan || 1);
        continue;
      }
      let value: any;
      try {
        value = valueToContent(d.value(ctx, item));
      } catch {
        value = { text: FALLBACK, style: 'fieldValue' };
      }
      out.push({
        label: String(d.label || '').replace(/:$/, ''),
        value,
        width: d.width,
        colSpan: d.colSpan,
      });
    }
    return out;
  };

  const renderSectionDetail = (layout: any, item?: any) => {
    const fields = layoutToFields(layout, item);
    if (!fields.length) return { text: '' };

    // Use variable-width grid if any field has a custom width
    const hasCustomWidths = fields.some((f: any) => f.width !== undefined);
    if (hasCustomWidths) {
      return createDetailGridWithWidths(fields, layout.columnsPerRow);
    }
    return createDetailGrid(fields as any, layout.columnsPerRow);
  };

  // --- Build content ---
  const content: any[] = [];

  // TOP SECTION (page 1 only, no title)
  if (cfsTopSectionFields.detail) {
    const topFields = layoutToFields(cfsTopSectionFields.detail);
    const topGrid = createDetailGridWithWidths(
      topFields,
      cfsTopSectionFields.detail.columnsPerRow,
    );
    content.push({
      ...topGrid,
      margin: [0, 0, 0, 10] as [number, number, number, number],
    });
  }

  // CFS DETAILS
  const details = cfsReportSections.find((s) => s.key === 'cfsDetails');
  if (details?.detail) {
    content.push(
      createCard(details.title, renderSectionDetail(details.detail), true),
    );
  }

  // --- Helper for repeating sections (units, names, vehicles) ---
  const renderRepeatingSection = (
    section: CFSReportSectionDef,
    addBorder: boolean,
  ) => {
    const { detail } = section;
    if (!detail) return;
    const items = section.items?.(ctx) || [];
    if (!items.length) {
      content.push(
        createCard(section.title, renderSectionDetail(detail), addBorder),
      );
    } else {
      const cards = items.map((item: any, idx: number) => ({
        ...(renderSectionDetail(detail, item) as any),
        margin: [0, idx > 0 ? 3 : 0, 0, 0] as [number, number, number, number],
      }));
      content.push(
        createCard(section.title, { stack: cards } as any, addBorder),
      );
    }
  };

  // REPORTER INFORMATION
  const reporter = cfsReportSections.find(
    (s) => s.key === 'reporterInformation',
  );
  if (reporter?.detail) {
    content.push(
      createCard(reporter.title, renderSectionDetail(reporter.detail), true),
    );
  }

  // UNITS
  const unitsSection = cfsReportSections.find((s) => s.key === 'units');
  if (unitsSection) renderRepeatingSection(unitsSection, true);

  // OFFICERS INVOLVED (table — keeps render)
  const officers = cfsReportSections.find((s) => s.key === 'officersInvolved');
  if (officers?.render) {
    content.push(createCard(officers.title, officers.render(ctx), true));
  }

  // NAMES (per-person cards)
  const namesSection = cfsReportSections.find((s) => s.key === 'names');
  if (namesSection) renderRepeatingSection(namesSection, false);

  // VEHICLES
  const vehiclesSection = cfsReportSections.find((s) => s.key === 'vehicles');
  if (vehiclesSection) renderRepeatingSection(vehiclesSection, true);

  // NARRATIVE
  const narrativeSection = cfsReportSections.find((s) => s.key === 'narrative');
  if (narrativeSection) renderRepeatingSection(narrativeSection, true);

  return {
    header,
    footer,
    content,
    styles: pdfStyles,
    pageMargins,
    pageSize: 'A4',
    pageOrientation: 'portrait',
    defaultStyle: {
      fontSize: 10,
      color: '#000',
    },
  } as any;
};
